import {
  OmsExportPreset,
  OmsPresetScope,
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { OmsFilterCondition } from "../../hooks/useOmsFilterCriteria";
import { OmsColumnsDraft } from "./OMSColumns";
import { submittedCondition } from "./omsFilterEditing";

export const presetScopeLabel: Record<OmsPresetScope, string> = {
  filters: "Rows",
  columns: "Columns",
  both: "Rows and columns",
};

export type OmsConfiguration = {
  mode: "full" | "filtered";
  criteria: OmsFilterCondition[];
  operation: "and" | "or";
  columns: OmsColumnsDraft;
  appliedPreset?: OmsExportPreset;
};

function filterSignature(filters: OmsRecordFilter[]) {
  return filters.map(({ column, operator, value, values }) => ({
    column,
    operator,
    ...(value !== undefined ? { value } : {}),
    ...(values !== undefined ? { values: [...values].sort() } : {}),
  }));
}

export function presetIsEdited(
  preset: OmsExportPreset,
  draft: OmsConfiguration
): boolean {
  const filtersChanged =
    preset.scope !== "columns" &&
    ((preset.rows === "all") !== (draft.mode === "full") ||
      (preset.operation || "and") !==
        (draft.mode === "full" ? "and" : draft.operation) ||
      JSON.stringify(filterSignature(preset.filters || [])) !==
        JSON.stringify(
          filterSignature(
            draft.mode === "full" ? [] : draft.criteria.map(submittedCondition)
          )
        ));
  const columnsChanged =
    preset.scope !== "filters" &&
    (preset.columns?.strategy !== draft.columns.strategy ||
      (draft.columns.strategy === "selected" &&
        JSON.stringify(preset.columns?.names) !==
          JSON.stringify(draft.columns.names)));
  return filtersChanged || columnsChanged;
}

export function applyPreset(
  preset: OmsExportPreset,
  draft: OmsConfiguration,
  report: OmsReport
): OmsConfiguration {
  const filters = (preset.filters || []).map((filter, id) => {
    const oldType = preset.schemaTypes[filter.column] || "string";
    const newType = report.columnTypes?.[filter.column] || "string";
    return {
      ...filter,
      id,
      ...(report.columns.includes(filter.column) && oldType !== newType
        ? {
            valueNotice: `This field now uses ${newType}. Review its condition and value.`,
          }
        : {}),
    };
  });
  return {
    ...draft,
    ...(preset.scope !== "columns"
      ? {
          mode: preset.rows === "all" ? "full" : "filtered",
          criteria: preset.rows === "all" ? draft.criteria : filters,
          operation:
            preset.rows === "all" ? draft.operation : preset.operation || "and",
        }
      : {}),
    ...(preset.scope !== "filters"
      ? {
          columns: {
            strategy: preset.columns?.strategy || "selected",
            names:
              preset.columns?.strategy === "all"
                ? draft.columns.names
                : [...(preset.columns?.names || [])],
            touched: true,
            customOrder: true,
          },
        }
      : {}),
    appliedPreset: preset,
  };
}
